import { useState, useEffect, type FormEvent } from "react";
import { useToast, useColorModeValue } from "@chakra-ui/react";
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalCloseButton, ModalFooter,
  Button, FormControl, FormLabel, Input, Select, VStack, Text, Checkbox, SimpleGrid, Box,
} from "@chakra-ui/react";
import { adminApi } from "../../../data/axios/adminApi";
import { AdminUser, AdminRole } from "../../../types/admin";

const COUNTRIES = [
  { code: "BR", name: "Brasil" },
  { code: "AR", name: "Argentina" },
  { code: "CL", name: "Chile" },
  { code: "CO", name: "Colômbia" },
  { code: "MX", name: "México" },
  { code: "PE", name: "Peru" },
  { code: "UY", name: "Uruguai" },
  { code: "PY", name: "Paraguai" },
  { code: "BO", name: "Bolívia" },
  { code: "EC", name: "Equador" },
  { code: "PT", name: "Portugal" },
  { code: "US", name: "Estados Unidos" },
];

interface Props {
  isOpen: boolean;
  onClose: () => void;
  user: AdminUser | null;
  availableRoles: AdminRole[];
  onSuccess: () => void;
}

export function UserEditModal({ isOpen, onClose, user, availableRoles, onSuccess }: Props) {
  const toast = useToast();

  const titleColor  = useColorModeValue("#434347", "#e8edf2");
  const descColor   = useColorModeValue("#696a71", "#8fa3b8");
  const labelColor  = useColorModeValue("#434347", "#e8edf2");
  const cardBorder  = useColorModeValue("rgba(0,0,0,0.08)", "rgba(255,255,255,0.1)");
  const inputBg     = useColorModeValue("white", "rgba(255,255,255,0.05)");
  const inputBorder = useColorModeValue("rgba(0,0,0,0.15)", "rgba(255,255,255,0.15)");

  const [name, setName] = useState("");
  const [country, setCountry] = useState("");
  const [selectedRoleIds, setSelectedRoleIds] = useState<number[]>([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (user) {
      setName(user.name);
      setCountry(user.country || "");
      const currentIds = availableRoles
        .filter((r) => user.roles.includes(r.name))
        .map((r) => r.id);
      setSelectedRoleIds(currentIds);
    }
  }, [user, isOpen, availableRoles]);

  const toggleRole = (id: number) => {
    setSelectedRoleIds((prev) =>
      prev.includes(id) ? prev.filter((rid) => rid !== id) : [...prev, id],
    );
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!user) return;
    try {
      setSubmitting(true);
      await adminApi.updateUser(user.id, {
        name,
        country: country || undefined,
        roles: selectedRoleIds,
      });
      onSuccess();
      onClose();
    } catch {
      toast({ title: "Erro ao atualizar dados do usuário", status: "error", duration: 4000, isClosable: true });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay />
      <ModalContent as="form" onSubmit={handleSubmit} borderRadius="2xl" mx={4}>
        <ModalHeader
          fontSize="lg"
          fontWeight={600}
          color={titleColor}
          borderBottomWidth="1px"
          borderColor={cardBorder}
          pb={4}
        >
          Editar Colaborador
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody pt={5}>
          <VStack spacing={4} align="stretch">
            <Box p={3} borderRadius="8px" bg={useColorModeValue("rgba(0,158,251,0.06)", "rgba(0,158,251,0.08)")} borderWidth="1px" borderColor="rgba(0,158,251,0.2)">
              <Text fontSize="xs" color={descColor}>
                E-mail: <Text as="span" fontWeight={600} color={labelColor}>{user?.email}</Text>
              </Text>
            </Box>

            <FormControl isRequired>
              <FormLabel fontSize="sm" fontWeight={600} color={labelColor} mb={1}>Nome Completo</FormLabel>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                bg={inputBg}
                borderColor={inputBorder}
                borderRadius="8px"
                fontSize="sm"
                _focus={{ borderColor: "#009efb", boxShadow: "0 0 0 2px rgba(0,158,251,0.15)" }}
              />
            </FormControl>

            <FormControl>
              <FormLabel fontSize="sm" fontWeight={600} color={labelColor} mb={2}>Papéis</FormLabel>
              <SimpleGrid columns={2} spacing={2}>
                {availableRoles.map((role) => (
                  <Checkbox
                    key={role.id}
                    isChecked={selectedRoleIds.includes(role.id)}
                    onChange={() => toggleRole(role.id)}
                    colorScheme="blue"
                    size="sm"
                  >
                    <Text fontSize="sm" color={labelColor}>{role.name}</Text>
                  </Checkbox>
                ))}
              </SimpleGrid>
            </FormControl>

            <FormControl>
              <FormLabel fontSize="sm" fontWeight={600} color={labelColor} mb={1}>País</FormLabel>
              <Select
                placeholder="Selecione um país"
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                bg={inputBg}
                borderColor={inputBorder}
                borderRadius="8px"
                fontSize="sm"
                _focus={{ borderColor: "#009efb", boxShadow: "0 0 0 2px rgba(0,158,251,0.15)" }}
              >
                {COUNTRIES.map((c) => (
                  <option key={c.code} value={c.code}>{c.name}</option>
                ))}
              </Select>
            </FormControl>
          </VStack>
        </ModalBody>

        <ModalFooter borderTopWidth="1px" borderColor={cardBorder} gap={2} pt={4}>
          <Button variant="ghost" borderRadius="50px" size="sm" onClick={onClose}>Cancelar</Button>
          <Button
            bg="#009efb"
            color="white"
            _hover={{ bg: "#007bd4" }}
            borderRadius="50px"
            size="sm"
            type="submit"
            isLoading={submitting}
          >
            Salvar Alterações
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
